//src/components/Calendar.js
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import Header from './Header';

const Calendar = () => {
  // Quản lý trạng thái
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userTeams, setUserTeams] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showOnlyMyTasks, setShowOnlyMyTasks] = useState(false); // Trạng thái mới: chỉ hiển thị công việc được giao cho tôi
  
  // Hàm để chuyển đổi URL thành liên kết có thể nhấp được
  const linkifyText = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const result = [];
    let lastIndex = 0;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push(text.substring(lastIndex, match.index));
      }
      let url = match[0];
      // Nếu là www. mà không có http thì thêm http://
      const href = url.startsWith('http') ? url : `http://${url}`;
      result.push(
        <a key={`link-${match.index}`} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{url}</a>
      );
      lastIndex = match.index + url.length;
    }
    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex));
    }
    return result;
  };

  // Lấy dữ liệu người dùng và phòng ban
  useEffect(() => {
    const fetchUserData = async () => {
      if (!auth.currentUser) return;
      
      try {
        setLoading(true);
        const userId = auth.currentUser.uid;
        
        // Lấy dữ liệu người dùng
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userInfo = userSnap.data();
          const userData = { uid: userId, ...userInfo };
          setUserData(userData);

          // Kiểm tra system admin qua email
          const email = userInfo.email || '';
          const isSystemAdmin = email === 'ha.ttt@greenschool.edu.vn' || email === 'maxdevfs@gmail.com';

          // Lấy danh sách phòng ban của người dùng hoặc tất cả nếu admin hệ thống
          let teamsQuery;
          if (isSystemAdmin) {
            teamsQuery = query(collection(db, 'teams'));
          } else {
            teamsQuery = query(
              collection(db, 'teams'),
              where('members', 'array-contains', userId)
            );
          }
          
          const teamsSnap = await getDocs(teamsQuery);
          const teamsData = [];
           
          teamsSnap.forEach(doc => {
            teamsData.push({
              id: doc.id,
              ...doc.data()
            });
          });

          // Chỉ lấy các phòng ban công khai
          const publicTeams = teamsData.filter(t => !t.private);
          setUserTeams(publicTeams);
          
          // Lấy tất cả công việc từ các phòng ban công khai
          await fetchAllTeamTasks(publicTeams);
        }
      } catch (error) {
        console.error('Lỗi khi lấy dữ liệu người dùng:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, []);
  
  // Lấy tất cả công việc từ các phòng ban
  const fetchAllTeamTasks = async (teams) => {
    if (!teams || teams.length === 0) return;
    
    try {
      const allTasks = [];
      
      for (const team of teams) {
        const teamRef = doc(db, 'teams', team.id);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          
          if (teamData.tasks && Array.isArray(teamData.tasks)) {
            // Thêm thông tin phòng ban vào mỗi công việc
            const tasksWithTeamInfo = teamData.tasks.map(task => ({
              ...task,
              teamId: team.id,
              teamName: team.name,
              teamColor: team.color || getRandomColor(team.id)
            }));
            
            allTasks.push(...tasksWithTeamInfo);
          }
        }
      }
      
      setTasks(allTasks);
    } catch (error) {
      console.error('Lỗi khi lấy công việc của phòng ban:', error);
    }
  };
  
  // Tạo màu ngẫu nhiên từ ID phòng ban
  const getRandomColor = (teamId) => {
    const colors = [
      '#10B981', // Xanh lá
      '#3B82F6', // Xanh dương
      '#8B5CF6', // Tím
      '#EC4899', // Hồng
      '#F59E0B', // Cam
      '#EF4444', // Đỏ
      '#06B6D4', // Xanh lam
      '#6366F1'  // Chàm
    ];
    
    // Sử dụng ID phòng ban làm hạt giống để chọn màu, đảm bảo cùng một phòng ban luôn có cùng màu
    const index = teamId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };
  
  // Kiểm tra xem công việc có được giao cho người dùng hiện tại không
  const isTaskAssignedToMe = (task) => {
    if (!userData || !userData.uid) return false;
    if (!task.assignees || !Array.isArray(task.assignees)) return false;
    
    return task.assignees.some(assignee => 
      (typeof assignee === 'string' && assignee === userData.uid) || 
      (assignee && assignee.uid === userData.uid)
    );
  };
  
  // Lọc công việc
  const filteredTasks = tasks
    // Lọc theo phòng ban
    .filter(task => selectedTeam === 'all' || task.teamId === selectedTeam)
    // Lọc theo người được giao
    .filter(task => !showOnlyMyTasks || isTaskAssignedToMe(task));
  
  // Xử lý bộ lọc công việc
  const toggleMyTasksFilter = () => {
    setShowOnlyMyTasks(!showOnlyMyTasks);
  };
  
  // Lấy phạm vi ngày dựa trên chế độ xem
  const getDateRange = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (viewMode === 'month') {
      // Lấy ngày đầu tiên của tháng
      const firstDay = new Date(year, month, 1);
      // Lấy ngày cuối cùng của tháng
      const lastDay = new Date(year, month + 1, 0);
      
      // Điều chỉnh để hiển thị đầy đủ các tuần
      const firstDayOfWeek = firstDay.getDay(); // 0 là chủ nhật, 1 là thứ hai
      const start = new Date(firstDay);
      start.setDate(firstDay.getDate() - firstDayOfWeek);
      
      const lastDayOfWeek = lastDay.getDay();
      const end = new Date(lastDay);
      end.setDate(lastDay.getDate() + (6 - lastDayOfWeek));
      
      return { start, end };
    } else if (viewMode === 'week') {
      // Lấy ngày chủ nhật của tuần hiện tại
      const dayOfWeek = currentDate.getDay();
      const start = new Date(currentDate);
      start.setDate(currentDate.getDate() - dayOfWeek);
      
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      
      return { start, end };
    } else { // day
      const start = new Date(currentDate);
      const end = new Date(currentDate);
      return { start, end };
    }
  };
  
  // Tạo mảng các ngày
  const generateDates = () => {
    const { start, end } = getDateRange();
    const dates = [];
    const current = new Date(start);
    
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  };
  
  // Tính số tuần trong một tháng
  const getWeeksInMonth = (dates) => {
    if (!dates.length) return 0;
    
    return Math.ceil(dates.length / 7);
  };
  
  // Định dạng hiển thị ngày
  const formatDate = (date, format = 'short') => {
    if (format === 'full') {
      return date.toLocaleDateString('vi-VN', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } else if (format === 'medium') {
      return date.toLocaleDateString('vi-VN', { 
        month: 'short', 
        day: 'numeric' 
      });
    } else if (format === 'time') {
      return date.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      return date.getDate().toString();
    }
  };
  
  // Điều hướng đến thời gian trước đó
  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    
    setCurrentDate(newDate);
  };
  
  // Điều hướng đến thời gian tiếp theo
  const navigateNext = () => {
    const newDate = new Date(currentDate);
    
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    
    setCurrentDate(newDate);
  };
  
  // Chuyển đến hôm nay
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Thay đổi chế độ xem
  const changeViewMode = (mode) => {
    setViewMode(mode);
  };
  
  // Xử lý khi nhấp vào ngày
  const handleDateClick = (date) => {
    setSelectedDate(date);
    
    // Nếu đang ở chế độ xem ngày, cập nhật ngày hiện tại
    if (viewMode === 'day') {
      setCurrentDate(date);
    }
  };
  
  // Xử lý khi nhấp vào công việc
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };
  
  // Lấy công việc cho một ngày cụ thể
  const getTaskEvents = (date) => {
    return filteredTasks.filter(task => {
      // Phân tích ngày bắt đầu công việc
      let taskStart;
      if (task.start_date) {
        if (typeof task.start_date.toDate === 'function') {
          taskStart = task.start_date.toDate();
        } else if (task.start_date._seconds) {
          taskStart = new Date(task.start_date._seconds * 1000);
        } else {
          taskStart = new Date(task.start_date);
        }
      } else {
        return false;
      }
      
      // Tính ngày kết thúc
      const duration = task.duration || 1;
      const taskEnd = new Date(taskStart);
      taskEnd.setDate(taskStart.getDate() + duration - 1);
      
      // Kiểm tra xem ngày công việc có trùng với ngày hiện tại không
      return (
        date.getFullYear() === taskStart.getFullYear() &&
        date.getMonth() === taskStart.getMonth() &&
        date.getDate() === taskStart.getDate()
      ) || (
        date >= taskStart && date <= taskEnd
      );
    });
  };
  
  // Tạo lịch cho chế độ xem hiện tại
  const dates = generateDates();
  const weeksCount = getWeeksInMonth(dates);
  
  // Nhãn các ngày trong tuần
  const weekdayLabels = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
  
  // Kiểm tra xem ngày có phải là hôm nay không
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };
  
  // Kiểm tra xem ngày có thuộc tháng hiện tại không
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };
  
  // Kiểm tra xem ngày có được chọn không
  const isSelectedDate = (date) => {
    return date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear();
  };
  
  // Hiển thị chế độ xem tháng
  const renderMonthView = () => {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* Nhãn ngày trong tuần */}
        <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
          {weekdayLabels.map((day, index) => (
            <div key={day} className="py-2 text-sm font-medium text-center text-gray-500 dark:text-gray-400">
              {day}
            </div>
          ))}
        </div>
        
        {/* Lưới ngày */}
        <div className="grid grid-cols-7 auto-rows-auto">
          {dates.map((date, index) => {
            const dateEvents = getTaskEvents(date);
            const isCurrentMonthDate = isCurrentMonth(date);
            
            return (
              <div 
                key={index}
                onClick={() => handleDateClick(date)}
                className={`min-h-[100px] lg:min-h-[120px] p-2 border-b border-r border-gray-200 dark:border-gray-700 ${
                  isToday(date) 
                    ? 'bg-emerald-50 dark:bg-emerald-900/20' 
                    : isSelectedDate(date)
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                } ${
                  !isCurrentMonthDate ? 'text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-800/50' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${
                    isToday(date) 
                      ? 'text-emerald-600 dark:text-emerald-400' 
                      : isCurrentMonthDate
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-400 dark:text-gray-600'
                  }`}>
                    {date.getDate()}
                  </span>
                  {isToday(date) && (
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 rounded-full">
                      Hôm nay
                    </span>
                  )}
                </div>
                
                {/* Danh sách công việc */}
                <div className="space-y-1 mt-1 overflow-y-auto max-h-[80px]">
                  {dateEvents.slice(0, 3).map((event, idx) => (
                    <div 
                      key={`${event.id}-${idx}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskClick(event);
                      }}
                      className="px-2 py-1 text-xs font-medium truncate rounded cursor-pointer"
                      style={{ backgroundColor: `${event.teamColor}30`, color: event.teamColor }}
                    >
                      {event.text}
                    </div>
                  ))}
                  {dateEvents.length > 3 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 pl-2">
                      +{dateEvents.length - 3} công việc khác
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  // Hiển thị chế độ xem tuần
  const renderWeekView = () => {
    const { start } = getDateRange();
    const weekDates = Array(7).fill(0).map((_, i) => {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      return date;
    });
    
    // Nhãn giờ
    const hourLabels = Array(24).fill(0).map((_, i) => {
      const hour = i % 12 === 0 ? 12 : i % 12;
      const period = i < 12 ? 'AM' : 'PM';
      return `${hour} ${period}`;
    });
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* Tiêu đề tuần */}
        <div className="grid grid-cols-8 border-b border-gray-200 dark:border-gray-700">
          <div className="py-2 text-sm font-medium text-center text-gray-500 dark:text-gray-400">
            Giờ
          </div>
          {weekDates.map((date, index) => (
            <div 
              key={index} 
              className={`py-2 text-sm font-medium text-center ${
                isToday(date) ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <div>{weekdayLabels[date.getDay()]}</div>
              <div className="text-xs">{formatDate(date, 'medium')}</div>
              {isToday(date) && (
                <div className="mt-1 mx-auto w-2 h-2 rounded-full bg-emerald-500"></div>
              )}
            </div>
          ))}
        </div>
        
        {/* Bảng thời gian */}
        <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
          <div className="relative grid grid-cols-8">
            {/* Thước thời gian */}
            <div className="border-r border-gray-200 dark:border-gray-700">
              {hourLabels.map((hour, index) => (
                <div 
                  key={index} 
                  className="h-16 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700"
                >
                  {hour}
                </div>
              ))}
            </div>
            
            {/* Lịch từng ngày */}
            {weekDates.map((date, dateIndex) => (
              <div key={dateIndex} className="relative">
                {/* Ô thời gian */}
                {hourLabels.map((_, hourIndex) => (
                  <div 
                    key={hourIndex} 
                    className="h-16 border-b border-r border-gray-200 dark:border-gray-700"
                  ></div>
                ))}
                
                {/* Công việc của ngày */}
                {getTaskEvents(date).map((event, eventIndex) => {
                  // Phân tích thời gian bắt đầu công việc
                  let startDate;
                  if (event.start_date) {
                    if (typeof event.start_date.toDate === 'function') {
                      startDate = event.start_date.toDate();
                    } else if (event.start_date._seconds) {
                      startDate = new Date(event.start_date._seconds * 1000);
                    } else {
                      startDate = new Date(event.start_date);
                    }
                  } else {
                    startDate = new Date();
                  }
                  
                  // Tính vị trí hiển thị
                  const hourHeight = 64; // 16px * 4 = 64px cho mỗi giờ (tương ứng h-16)
                  const startHour = startDate.getHours() + startDate.getMinutes() / 60;
                  const duration = event.duration || 1; // Sử dụng số ngày làm tham số gần đúng
                  const heightInHours = Math.min(duration * 4, 24 - startHour); // Giới hạn chiều cao không vượt quá ngày
                  
                  const top = startHour * hourHeight;
                  const height = heightInHours * hourHeight;
                  
                  return (
                    <div
                      key={eventIndex}
                      onClick={() => handleTaskClick(event)}
                      className="absolute left-0 right-0 mx-1 p-1 rounded-sm truncate overflow-hidden text-xs shadow-sm cursor-pointer"
                      style={{ 
                        top: `${top}px`, 
                        height: `${height}px`,
                        backgroundColor: `${event.teamColor}30`,
                        color: event.teamColor,
                        borderLeft: `3px solid ${event.teamColor}`
                      }}
                    >
                      <div className="font-medium">{event.text}</div>
                      <div>{formatDate(startDate, 'time')}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  // Hiển thị chế độ xem ngày
  const renderDayView = () => {
    // Nhãn giờ
    const hourLabels = Array(24).fill(0).map((_, i) => {
      const hour = i % 12 === 0 ? 12 : i % 12;
      const period = i < 12 ? 'AM' : 'PM';
      return `${hour} ${period}`;
    });
    
    const dateEvents = getTaskEvents(currentDate);
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* Tiêu đề ngày */}
        <div className="py-3 px-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {formatDate(currentDate, 'full')}
            {isToday(currentDate) && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 rounded-full">
                Hôm nay
              </span>
            )}
          </h3>
        </div>
        
        {/* Bảng thời gian */}
        <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
          <div className="relative grid grid-cols-12">
            {/* Thước thời gian */}
            <div className="col-span-1 border-r border-gray-200 dark:border-gray-700">
              {hourLabels.map((hour, index) => (
                <div 
                  key={index} 
                  className="h-20 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700"
                >
                  {hour}
                </div>
              ))}
            </div>
            
            {/* Khu vực công việc của ngày */}
            <div className="col-span-11 relative">
              {/* Ô thời gian */}
              {hourLabels.map((_, hourIndex) => (
                <div 
                  key={hourIndex} 
                  className="h-20 border-b border-gray-200 dark:border-gray-700"
                ></div>
              ))}
              
              {/* Công việc */}
              {dateEvents.map((event, eventIndex) => {
                // Phân tích thời gian bắt đầu công việc
                let startDate;
                if (event.start_date) {
                  if (typeof event.start_date.toDate === 'function') {
                    startDate = event.start_date.toDate();
                  } else if (event.start_date._seconds) {
                    startDate = new Date(event.start_date._seconds * 1000);
                  } else {
                    startDate = new Date(event.start_date);
                  }
                } else {
                  startDate = new Date();
                }
                
                // Tính vị trí hiển thị
                const hourHeight = 80; // 20px * 4 = 80px cho mỗi giờ (tương ứng h-20)
                const startHour = startDate.getHours() + startDate.getMinutes() / 60;
                const duration = event.duration || 1;
                // Chuyển đổi ngày sang giờ (gần đúng) cho mục đích hiển thị
                const heightInHours = Math.min(duration * 3, 24 - startHour);
                
                const top = startHour * hourHeight;
                const height = heightInHours * hourHeight;
                
                return (
                  <div
                    key={eventIndex}
                    onClick={() => handleTaskClick(event)}
                    className="absolute left-4 right-4 p-2 rounded shadow-md cursor-pointer overflow-hidden"
                    style={{ 
                      top: `${top}px`, 
                      height: `${height}px`,
                      backgroundColor: `${event.teamColor}20`,
                      color: event.teamColor,
                      borderLeft: `4px solid ${event.teamColor}`
                    }}
                  >
                    <div className="font-medium">{event.text}</div>
                    <div className="text-xs">{formatDate(startDate, 'time')}</div>
                    <div className="text-xs mt-1 text-gray-600 dark:text-gray-300">
                      {event.teamName}
                    </div>
                    {event.description && (
                      <div className="text-xs mt-1 line-clamp-2 text-gray-500 dark:text-gray-400">
                        {linkifyText(event.description)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Hiển thị cửa sổ chi tiết công việc
  const renderTaskModal = () => {
    if (!selectedTask) return null;
    
    // Phân tích ngày bắt đầu công việc
    let startDate;
    if (selectedTask.start_date) {
      if (typeof selectedTask.start_date.toDate === 'function') {
        startDate = selectedTask.start_date.toDate();
      } else if (selectedTask.start_date._seconds) {
        startDate = new Date(selectedTask.start_date._seconds * 1000);
      } else {
        startDate = new Date(selectedTask.start_date);
      }
    } else {
      startDate = new Date();
    }
    
    // Tính ngày kết thúc
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + (selectedTask.duration || 1) - 1);
    
    // Kiểu màu cho trạng thái
    const getStatusColor = (status) => {
      switch (status) {
        case 'completed':
          return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300';
        case 'inProgress':
        case 'in-progress':
          return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
        case 'on-hold':
          return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300';
        case 'canceled': // Bổ sung trạng thái canceled
          return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
        default:
          return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      }
    };
    
    // Kiểu màu cho ưu tiên
    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'high':
          return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
        case 'medium':
          return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
        case 'low':
          return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
        default:
          return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      }
    };
    
    // Chuyển đổi tên trạng thái
    const getStatusName = (status) => {
      switch (status) {
        case 'notStarted': 
          return 'Chưa bắt đầu';
        case 'inProgress':
        case 'in-progress':
          return 'Đang thực hiện';
        case 'completed':
          return 'Đã hoàn thành';
        case 'on-hold':
          return 'Tạm dừng';
        case 'canceled': // Bổ sung trạng thái canceled
          return 'Đã hủy';
        default:
          return status ? status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Không xác định';
      }
    };
    
    // Chuyển đổi tên ưu tiên
    const getPriorityName = (priority) => {
      switch (priority) {
        case 'high':
          return 'Ưu tiên cao';
        case 'medium':
          return 'Ưu tiên trung bình';
        case 'low':
          return 'Ưu tiên thấp';
        default:
          return 'Không xác định';
      }
    };
    
    return (
      <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="task-modal-title" role="dialog" aria-modal="true">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/* Lớp phủ nền */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            aria-hidden="true"
            onClick={() => setShowTaskModal(false)}
          ></div>
          
          {/* Kỹ thuật căn giữa modal */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          
          {/* Nội dung modal */}
          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            {/* Thanh tiêu đề */}
            <div className="py-4 px-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="task-modal-title">
                  Chi tiết công việc
                </h3>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
                  onClick={() => setShowTaskModal(false)}
                >
                  <span className="sr-only">Đóng</span>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            
            {/* Chi tiết công việc */}
            <div className="py-4 px-6">
              <div className="space-y-4">
                {/* Tên công việc và trạng thái */}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {selectedTask.text}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTask.status)}`}>
                      {getStatusName(selectedTask.status)}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(selectedTask.priority)}`}>
                      {getPriorityName(selectedTask.priority)}
                    </span>
                    <span
                      className="px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${selectedTask.teamColor}20`, color: selectedTask.teamColor }}
                    >
                      {selectedTask.teamName}
                    </span>
                  </div>
                </div>
                
                {/* Ngày và tiến độ */}
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <i className="fas fa-calendar-alt mr-2"></i>
                    <span>{formatDate(startDate, 'full')}</span>
                    {selectedTask.duration > 1 && (
                      <>
                        <span className="mx-2">-</span>
                        <span>{formatDate(endDate, 'full')}</span>
                      </>
                    )}
                  </div>
                  
                  <div className="flex flex-col">
                    <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-1">
                      <span>Tiến độ</span>
                      <span>{selectedTask.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div 
                        className="bg-emerald-500 h-2.5 rounded-full" 
                        style={{ width: `${selectedTask.progress || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                {/* Mô tả */}
                {selectedTask.description && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mô tả</h5>
                    <p className="text-sm text-gray-500 dark:text-gray-400 whitespace-pre-line">{linkifyText(selectedTask.description)}</p>
                  </div>
                )}
                
                {/* Người phụ trách */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Người phụ trách</h5>
                  {selectedTask.assignees && selectedTask.assignees.length > 0 ? (
                    <div className="space-y-2">
                      {selectedTask.assignees.map((assignee, index) => {
                        // Kiểm tra xem có phải người dùng hiện tại không
                        const isCurrentUser = userData && 
                          ((typeof assignee === 'string' && assignee === userData.uid) || 
                           (assignee.uid === userData.uid));
                        
                        return (
                          <div 
                            key={index} 
                            className={`flex items-center text-sm ${
                              isCurrentUser 
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-l-4 border-emerald-500 pl-2 py-1 rounded'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                              isCurrentUser 
                                ? 'bg-emerald-200 dark:bg-emerald-700'
                                : 'bg-gray-300 dark:bg-gray-700'
                            }`}>
                              <span className={`text-xs ${
                                isCurrentUser 
                                  ? 'text-emerald-700 dark:text-emerald-300'
                                  : 'text-gray-600 dark:text-gray-300'
                              }`}>
                                {(assignee.displayName || (typeof assignee === 'string' ? 'U' : assignee.email || 'U')).charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span>{assignee.displayName || (typeof assignee === 'string' ? 'Người dùng không xác định' : assignee.email || 'Người dùng không xác định')}</span>
                            {isCurrentUser && (
                              <span className="ml-2 text-xs bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200 px-2 py-0.5 rounded">Bạn</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      Không có người phụ trách
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Phần cuối modal */}
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-800 dark:bg-gray-600 text-base font-medium text-white hover:bg-gray-900 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => setShowTaskModal(false)}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tiêu đề lịch và thanh công cụ */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {viewMode === 'month' 
                ? currentDate.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })
                : viewMode === 'week'
                  ? `Tuần từ ${dates[0].toLocaleDateString('vi-VN', { month: 'short', day: 'numeric' })}`
                  : formatDate(currentDate, 'full')
              }
            </h1>
          </div>
          
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            {/* Nút điều hướng lịch */}
            <div className="flex bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <button
                onClick={goToToday}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-l-lg"
              >
                Hôm nay
              </button>
              <div className="flex border-l border-r border-gray-200 dark:border-gray-700">
                <button
                  onClick={navigatePrevious}
                  className="px-3 py-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button
                  onClick={navigateNext}
                  className="px-3 py-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              <div className="flex border-r border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => changeViewMode('month')}
                  className={`px-3 py-2 text-sm font-medium ${
                    viewMode === 'month' 
                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' 
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Tháng
                </button>
                <button
                  onClick={() => changeViewMode('week')}
                  className={`px-3 py-2 text-sm font-medium ${
                    viewMode === 'week' 
                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' 
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Tuần
                </button>
                <button
                  onClick={() => changeViewMode('day')}
                  className={`px-3 py-2 text-sm font-medium ${
                    viewMode === 'day' 
                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' 
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Ngày
                </button>
              </div>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="border-0 pl-3 pr-9 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-transparent focus:ring-0 focus:outline-none rounded-r-lg"
              >
                <option value="all">Tất cả phòng ban</option>
                {userTeams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
            
            {/* Công tắc chỉ hiển thị công việc của tôi */}
            <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg shadow-sm px-4 py-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-3">
                Chỉ công việc của tôi
              </span>
              <button 
                onClick={toggleMyTasksFilter}
                className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${
                  showOnlyMyTasks ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                aria-pressed={showOnlyMyTasks}
                aria-labelledby="my-tasks-filter"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showOnlyMyTasks ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        
        {/* Hiển thị lịch */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'day' && renderDayView()}
        </div>
        
        {/* Cửa sổ chi tiết công việc */}
        {showTaskModal && renderTaskModal()}
      </div>
    </div>
  );
};

export default Calendar;
